/**
 * Adapted from PlayCanvas Engine's fisheye-projection.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type * as THREE from 'three'

/** 缓存 Unified GSplat 鱼眼投影派生值，供 raster 与 cull 路径共用同一套公式。 */
export class FisheyeProjection {
  enabled = false
  k = 1
  invK = 1
  cornerScale = 1
  projMat00 = 1
  projMat11 = 1
  maxTheta = Math.PI

  private _lastT = -1
  private _lastFov = -1
  private _lastP00 = 0
  private _lastP11 = 0

  update(t: number, fov: number, projectionMatrix: THREE.Matrix4): void {
    const elements = projectionMatrix.elements
    if (elements[15] === 1) t = 0

    const p00 = elements[0]
    const p11 = elements[5]
    if (
      t === this._lastT &&
      fov === this._lastFov &&
      p00 === this._lastP00 &&
      p11 === this._lastP11
    ) {
      return
    }

    this._lastT = t
    this._lastFov = fov
    this._lastP00 = p00
    this._lastP11 = p11

    if (t <= 0) {
      this.enabled = false
      this.k = 1
      this.invK = 1
      this.cornerScale = 1
      this.maxTheta = Math.PI
      return
    }

    this.enabled = true
    const kMin = fov / 180 + 0.15
    const kStart = Math.max(1, fov / 180 + 0.05)
    const k = kStart * (kMin / kStart) ** t

    this.k = k
    this.invK = 1 / k
    this.cornerScale = 1 + (Math.SQRT2 - 1) * t

    const maxTheta = Math.min((k * Math.PI) / 2, 3.13)
    const halfFovX = Math.atan2(1, p00)
    const halfFovY = Math.atan2(1, p11)
    const effectiveHalfFovX = Math.min(halfFovX, maxTheta - 0.01)
    const effectiveHalfFovY = Math.min(halfFovY, maxTheta - 0.01)
    this.projMat00 = this.cornerScale / (k * Math.tan(effectiveHalfFovX / k))
    this.projMat11 = this.cornerScale / (k * Math.tan(effectiveHalfFovY / k))
    this.maxTheta = maxTheta
  }
}
